import { Alert, Button, Col, DatePicker, Drawer, Form, Input, Row, Select, Space, Spin, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import locale from 'antd/es/date-picker/locale/es_ES';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AdminHeader from 'src/globals/Layout/Admin/AdminHeader';
import { openNotification } from 'src/globals/Components/Notification/Notificaction';
import { getAllOrganizationAPI } from 'src/store/organization/restCallOrganization';
import { AppState } from 'src/store';
import { getUserByToken } from 'src/store/user/thunks';
import { UserState } from 'src/store/user/types';
import { getAllUsersTableAPI } from 'src/services/restcall';
import {
    AuditDetail,
    AuditListItem,
    AuditListQuery,
    getAuditDetailAPI,
    getAuditListAPI,
} from 'src/services/auditHistory';
import {
    ACTION_LABELS,
    ChangesView,
    ENTITY_LABELS,
    MODULE_LABELS,
    SOURCE_LABELS,
    actionColor,
    formatOccurredAt,
    isBlankOrUuid,
    labelOf,
    toOptions,
} from './auditDisplay';
import styles from './AuditHistory.module.scss';

export const SUPERADMIN_GROUP_ID = 'ec438afc-63f2-4acf-b1d4-e51df3043190';

const EMPTY_MESSAGE = 'No se encontraron registros para los filtros seleccionados.';
const FORBIDDEN_MESSAGE = 'Tu usuario no tiene permisos para consultar el historial.';
const SERVER_ERROR_MESSAGE = 'Existe un error por favor comuniquese con Ing. Pablo Santacruz';
const GENERIC_ERROR_MESSAGE = 'No se pudo consultar el historial.';

interface LookupOption {
    value: string;
    label: string;
}

interface FilterValues {
    range?: [Dayjs, Dayjs] | null;
    userId?: string;
    organizationId?: string;
    module?: string;
    entityType?: string;
    actionType?: string;
    entityId?: string;
    correlationId?: string;
}

interface AuditHistoryProps {
    user: UserState;
    getUserByToken?: any;
}

function textOf(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function readId(item: any): string {
    return textOf(item?.id ?? item?.Id);
}

function readName(item: any, keys: string[]): string {
    for (const key of keys) {
        const value = textOf(item?.[key]);
        if (value.trim() !== '') {
            return value.trim();
        }
    }
    return '';
}

function inclusiveUtcRange(range: [Dayjs, Dayjs] | null | undefined): { dateFrom?: string; dateTo?: string } {
    if (!range || !range[0] || !range[1]) {
        return {};
    }
    const dateFrom = range[0].startOf('day').toDate().toISOString();
    const dateTo = range[1].startOf('day').add(1, 'day').toDate().toISOString();
    return { dateFrom, dateTo };
}

function buildQuery(filters: FilterValues, page: number, pageSize: number): AuditListQuery {
    const dates = inclusiveUtcRange(filters.range);
    return {
        dateFrom: dates.dateFrom,
        dateTo: dates.dateTo,
        userId: filters.userId || undefined,
        organizationId: filters.organizationId || undefined,
        module: filters.module || undefined,
        entityType: filters.entityType || undefined,
        actionType: filters.actionType || undefined,
        entityId: filters.entityId?.trim() || undefined,
        correlationId: filters.correlationId?.trim() || undefined,
        page,
        pageSize,
    };
}

function notifyFailure(status: number) {
    if (status === 500) {
        openNotification('Advertencia', SERVER_ERROR_MESSAGE);
        return;
    }
    openNotification('Advertencia', GENERIC_ERROR_MESSAGE);
}

const AuditHistory: React.FC<AuditHistoryProps> = (props) => {
    const history = useHistory();
    const [form] = Form.useForm<FilterValues>();
    const [access, setAccess] = useState<'checking' | 'allowed' | 'denied'>('checking');
    const [loading, setLoading] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [items, setItems] = useState<AuditListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [users, setUsers] = useState<LookupOption[]>([]);
    const [organizations, setOrganizations] = useState<LookupOption[]>([]);
    const [organizationNames, setOrganizationNames] = useState<Record<string, string>>({});
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState<AuditDetail | null>(null);
    const appliedRef = useRef<FilterValues>({});
    const requestRef = useRef(0);

    const organizationLabel = (organizationId: string | null) => {
        if (!organizationId) {
            return '—';
        }
        return organizationNames[organizationId] ?? organizationId;
    };

    const loadList = async (nextPage: number, nextPageSize: number, filters: FilterValues) => {
        const requestId = ++requestRef.current;
        setLoading(true);
        setForbidden(false);
        try {
            const response = await getAuditListAPI(buildQuery(filters, nextPage, nextPageSize));
            if (requestId !== requestRef.current) {
                return;
            }
            if (response.StatusCode === 403) {
                setForbidden(true);
                setItems([]);
                setTotal(0);
                return;
            }
            if (response.StatusCode !== 200 || !response.Response) {
                setItems([]);
                setTotal(0);
                notifyFailure(response.StatusCode);
                return;
            }
            const body = response.Response;
            setItems(Array.isArray(body.items) ? body.items : []);
            setTotal(typeof body.total === 'number' ? body.total : 0);
            setPage(typeof body.page === 'number' ? body.page : nextPage);
            setPageSize(typeof body.pageSize === 'number' ? body.pageSize : nextPageSize);
        } catch {
            if (requestId !== requestRef.current) {
                return;
            }
            setItems([]);
            setTotal(0);
            openNotification('Advertencia', GENERIC_ERROR_MESSAGE);
        } finally {
            if (requestId === requestRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            const rol = await props.getUserByToken();
            if (cancelled) {
                return;
            }
            if (rol !== SUPERADMIN_GROUP_ID) {
                setAccess('denied');
                history.replace('/welcome');
                return;
            }
            setAccess('allowed');
            setLoading(true);
            const [userResponse, organizationResponse] = await Promise.all([
                getAllUsersTableAPI().catch(() => null),
                getAllOrganizationAPI().catch(() => null),
            ]);
            if (cancelled) {
                return;
            }
            if (userResponse?.StatusCode === 200 && Array.isArray(userResponse.Response)) {
                setUsers(userResponse.Response.map((item: any) => ({
                    value: readId(item),
                    label: readName(item, ['fullname', 'fullName', 'email']) || readId(item),
                })).filter((item: LookupOption) => item.value !== ''));
            }
            if (organizationResponse?.StatusCode === 200 && Array.isArray(organizationResponse.Response)) {
                const options = organizationResponse.Response.map((item: any) => ({
                    value: readId(item),
                    label: readName(item, ['name', 'Name']) || readId(item),
                })).filter((item: LookupOption) => item.value !== '');
                setOrganizations(options);
                const names: Record<string, string> = {};
                options.forEach((option: LookupOption) => {
                    names[option.value] = option.label;
                });
                setOrganizationNames(names);
            }
            await loadList(1, 20, {});
        };
        init();
        return () => {
            cancelled = true;
        };
    }, []);

    const onFilter = (values: FilterValues) => {
        if (!isBlankOrUuid(values.entityId) || !isBlankOrUuid(values.correlationId)) {
            openNotification('Advertencia', 'El identificador ingresado no es válido.');
            return;
        }
        const next: FilterValues = {
            range: values.range ?? null,
            userId: values.userId,
            organizationId: values.organizationId,
            module: values.module,
            entityType: values.entityType,
            actionType: values.actionType,
            entityId: values.entityId?.trim(),
            correlationId: values.correlationId?.trim(),
        };
        appliedRef.current = next;
        setPage(1);
        loadList(1, pageSize, next);
    };

    const onClear = () => {
        form.resetFields();
        appliedRef.current = {};
        setPage(1);
        loadList(1, pageSize, {});
    };

    const onTableChange = (pagination: TablePaginationConfig) => {
        const nextSize = pagination.pageSize ?? pageSize;
        const nextPage = nextSize !== pageSize ? 1 : (pagination.current ?? 1);
        setPage(nextPage);
        setPageSize(nextSize);
        loadList(nextPage, nextSize, appliedRef.current);
    };

    const openDetail = async (id: string) => {
        setDrawerOpen(true);
        setDetail(null);
        setDetailLoading(true);
        try {
            const response = await getAuditDetailAPI(id);
            if (response.StatusCode === 403) {
                setDetail(null);
                openNotification('Advertencia', FORBIDDEN_MESSAGE);
                return;
            }
            if (response.StatusCode === 404) {
                setDetail(null);
                openNotification('Advertencia', 'No se encontró el registro.');
                return;
            }
            if (response.StatusCode !== 200 || !response.Response) {
                setDetail(null);
                notifyFailure(response.StatusCode);
                return;
            }
            setDetail(response.Response as AuditDetail);
        } catch {
            setDetail(null);
            openNotification('Advertencia', GENERIC_ERROR_MESSAGE);
        } finally {
            setDetailLoading(false);
        }
    };

    const columns: ColumnsType<AuditListItem> = [
        {
            title: 'Fecha y hora',
            dataIndex: 'occurredAtUtc',
            width: 150,
            render: (value: string) => formatOccurredAt(value),
        },
        {
            title: 'Usuario',
            dataIndex: 'userDisplayName',
            width: 170,
            render: (value: string) => value || '—',
        },
        {
            title: 'Organización',
            dataIndex: 'organizationId',
            width: 190,
            render: (value: string | null) => (
                <Tooltip title={organizationLabel(value)}>
                    <span className={styles.cellText}>{organizationLabel(value)}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Módulo',
            dataIndex: 'module',
            width: 160,
            render: (value: string) => labelOf(MODULE_LABELS, value),
        },
        {
            title: 'Entidad',
            dataIndex: 'entityType',
            width: 190,
            render: (value: string) => labelOf(ENTITY_LABELS, value),
        },
        {
            title: 'Acción',
            dataIndex: 'actionType',
            width: 160,
            render: (value: string) => <Tag color={actionColor(value)}>{labelOf(ACTION_LABELS, value)}</Tag>,
        },
        {
            title: 'Descripción',
            dataIndex: 'description',
            render: (value: string) => (
                <Tooltip title={value}>
                    <span className={styles.cellText}>{value}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Detalle',
            key: 'detail',
            width: 120,
            fixed: 'right' as const,
            render: (_: unknown, record: AuditListItem) => (
                <Button type="link" onClick={() => openDetail(record.id)}>Ver detalle</Button>
            ),
        },
    ];

    if (access !== 'allowed') {
        return null;
    }

    return (
        <div>
            <AdminHeader>Historial de cambios</AdminHeader>
            <p className={styles.subtitle}>Registro de modificaciones realizadas en la plataforma.</p>
            {forbidden ? (
                <Alert className={styles.forbidden} type="error" showIcon message={FORBIDDEN_MESSAGE} />
            ) : (
                <>
                    <Form className={styles.filters} form={form} layout="vertical" onFinish={onFilter}>
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item label="Rango de fechas" name="range" extra="Incluye el día completo.">
                                    <DatePicker.RangePicker locale={locale} format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Usuario" name="userId">
                                    <Select allowClear showSearch optionFilterProp="label" options={users} placeholder="Todos" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Organización" name="organizationId">
                                    <Select allowClear showSearch optionFilterProp="label" options={organizations} placeholder="Todas" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Módulo" name="module">
                                    <Select allowClear options={toOptions(MODULE_LABELS)} placeholder="Todos" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Entidad" name="entityType">
                                    <Select allowClear showSearch optionFilterProp="label" options={toOptions(ENTITY_LABELS)} placeholder="Todas" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Acción" name="actionType">
                                    <Select allowClear options={toOptions(ACTION_LABELS)} placeholder="Todas" />
                                </Form.Item>
                            </Col>
                        </Row>
                        {/* <details className={styles.moreFilters}>
                            <summary>Más filtros</summary>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Entity ID" name="entityId">
                                        <Input allowClear placeholder="Identificador exacto" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Correlation ID" name="correlationId">
                                        <Input allowClear placeholder="Identificador exacto" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </details> */}
                        <div className={styles.actions}>
                            <Button type="primary" htmlType="submit">Filtrar</Button>
                            <Button onClick={onClear}>Limpiar</Button>
                        </div>
                    </Form>
                    <Table
                        size="small"
                        rowKey="id"
                        columns={columns}
                        dataSource={items}
                        loading={loading}
                        scroll={{ x: 1300 }}
                        locale={{ emptyText: loading ? ' ' : EMPTY_MESSAGE }}
                        pagination={{
                            current: page,
                            pageSize,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '50', '100'],
                            showTotal: (count) => `${count} registros`,
                        }}
                        onChange={onTableChange}
                    />
                </>
            )}
            <Drawer
                title="Detalle del cambio"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={720}
                destroyOnClose
            >
                {detailLoading ? <Spin /> : null}
                {!detailLoading && detail ? (
                    <div>
                        <Space direction="vertical" size={8} className={styles.drawerMeta}>
                            <div><strong>Fecha y hora:</strong> {formatOccurredAt(detail.occurredAtUtc)}</div>
                            <div><strong>Usuario:</strong> {detail.userDisplayName || '—'}</div>
                            <div><strong>Organización:</strong> {organizationLabel(detail.organizationId)}</div>
                            <div><strong>Módulo:</strong> {labelOf(MODULE_LABELS, detail.module)}</div>
                            <div><strong>Entidad:</strong> {labelOf(ENTITY_LABELS, detail.entityType)}</div>
                            <div><strong>Entity ID:</strong> <span className={styles.valueText}>{detail.entityId}</span></div>
                            <div><strong>Acción:</strong> <Tag color={actionColor(detail.actionType)}>{labelOf(ACTION_LABELS, detail.actionType)}</Tag></div>
                            <div><strong>Descripción:</strong> <span className={styles.valueText}>{detail.description}</span></div>
                            <div><strong>Correlation ID:</strong> <span className={styles.valueText}>{detail.correlationId}</span></div>
                            <div><strong>Source database:</strong> {labelOf(SOURCE_LABELS, detail.sourceDatabase)}</div>
                        </Space>
                        <ChangesView changes={detail.changes} />
                    </div>
                ) : null}
            </Drawer>
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export default connect(mapStateToProps, {
    getUserByToken,
})(AuditHistory);
